import type { RankedSpace } from "../types";
import type { RoomCombination } from "../hooks/useSearchAndRank";

/**
 * Combo grouping ("conjunto de salas").
 *
 * A combo is a backend-provided {@link RoomCombination}: a set of rooms in the
 * same building that together meet the capacity target. When the executive
 * selects every room of a combo, those rooms form a conjunto, identified by the
 * combo's `combinationId`. We persist those identities per room as `groupIds` so
 * the proposal and the client page can show the grouping after reload — a room
 * can belong to MORE THAN ONE combo, so it is a list, not a single id.
 */

/**
 * Map each room id to the `combinationId`s of every SELECTED combo it belongs to.
 * A combo is a first-class selection (its `combinationId` is in `selectedComboIds`),
 * independent of whether the executive also picked its rooms individually. Rooms
 * not in any selected combo are absent from the map (→ no groupIds / "avulsa").
 * A room shared by several selected combos gets one entry per combo, in order.
 */
export function deriveGroupIdsByRoom(
  combinations: RoomCombination[],
  selectedComboIds: string[],
): Record<string, string[]> {
  const selected = new Set(selectedComboIds);
  const map: Record<string, string[]> = {};
  for (const combo of combinations) {
    if (!selected.has(combo.combinationId)) continue;
    for (const r of combo.rooms) {
      const ids = (map[r.privateRoomId] ??= []);
      if (!ids.includes(combo.combinationId)) ids.push(combo.combinationId);
    }
  }
  return map;
}

/**
 * Rebuild `RoomCombination`s from the `groupIds` persisted on spaces.
 *
 * Combos are a search-time concept the backend does not store on the proposal —
 * only each room's `groupIds` survives. When a saved proposal is reopened for
 * editing we need the combinations back so the wizard can render/keep the combo
 * grouping (and so re-deriving `groupIds` doesn't wipe it). Every reconstructed
 * combo's rooms are, by construction, all present and selected, so downstream
 * "all rooms selected" checks pass. Order follows first appearance of each combo.
 */
export function reconstructRoomCombinations(
  spaces: RankedSpace[],
): RoomCombination[] {
  const byId = new Map<string, RoomCombination>();
  const order: string[] = [];
  for (const s of spaces) {
    for (const comboId of s.groupIds ?? []) {
      let combo = byId.get(comboId);
      if (!combo) {
        combo = {
          combinationId: comboId,
          spaceId: s.partnerId != null ? String(s.partnerId) : "",
          spaceName: s.spaceName ?? "",
          neighborhood: s.neighborhood ?? "",
          distanceKm: 0,
          rooms: [],
          totalCapacity: 0,
          totalMonthlyPrice: 0,
          score: 0,
        };
        byId.set(comboId, combo);
        order.push(comboId);
      }
      combo.rooms.push({
        privateRoomId: s.id,
        label: s.roomName ?? "",
        capacity: s.capacity ?? 0,
        monthlyPrice: s.monthlyPrice ?? 0,
      });
      combo.totalCapacity += s.capacity ?? 0;
      combo.totalMonthlyPrice += s.monthlyPrice ?? 0;
    }
  }
  return order.map((id) => byId.get(id)!);
}

/**
 * Stamp each space's `groupIds` from a `roomId → combinationId[]` map (see
 * {@link deriveGroupIdsByRoom}). Applied to every persisted bucket of rooms —
 * the ranked results AND the combo `extraSpaces` — so a combo room gets its
 * grouping marks regardless of which bucket it came from. Non-combo rooms get
 * `groupIds: undefined`.
 */
export function applyGroupIds<T extends { id: string; groupIds?: string[] }>(
  spaces: T[],
  groupIdsByRoom: Record<string, string[]>,
): T[] {
  return spaces.map((s) => ({ ...s, groupIds: groupIdsByRoom[s.id] }));
}

/** One render unit in the space list: a single room or a combo card. */
export type SpaceGroup<
  T extends Pick<RankedSpace, "groupIds" | "isIndividual">,
> =
  | { kind: "single"; space: T }
  | { kind: "combo"; comboId: string; spaces: T[] };

/**
 * Group an ordered list of spaces into render units, preserving order.
 *
 * A room emits a **single** unit (standalone card) when it was picked individually
 * (`isIndividual`) OR it belongs to no combo (`groupIds` empty) — the latter keeps
 * plain rooms rendering as singles. It also emits a **combo** unit for each of its
 * `groupIds`; each combo unit is anchored at the position of its FIRST member and
 * later members are absorbed into it. So a room that is both individual AND in a
 * combo renders as a standalone AND inside its combo(s), and a room in several
 * combos is replicated into each.
 */
export function groupRoomsIntoCombos<
  T extends Pick<RankedSpace, "groupIds" | "isIndividual">,
>(spaces: T[]): SpaceGroup<T>[] {
  const units: SpaceGroup<T>[] = [];
  const comboIndex = new Map<string, number>();
  for (const space of spaces) {
    const ids = space.groupIds ?? [];
    if (space.isIndividual || ids.length === 0) {
      units.push({ kind: "single", space });
    }
    for (const comboId of ids) {
      const existing = comboIndex.get(comboId);
      if (existing == null) {
        comboIndex.set(comboId, units.length);
        units.push({ kind: "combo", comboId, spaces: [space] });
      } else {
        (units[existing] as { kind: "combo"; spaces: T[] }).spaces.push(space);
      }
    }
  }
  return units;
}

/**
 * Aggregate the capacity and client price shown for one combo's rooms.
 *
 * Capacity is summed uniformly; the per-room price is resolved by the caller
 * (`priceOf`) because the source shape differs across surfaces — a
 * `SpaceAdjustment.clientPrice` (camelCase, with a `monthlyPrice` fallback) in
 * the wizard/proposal views vs the API's `client_price` (snake_case) on the
 * backoffice detail page.
 */
export function sumComboTotals<T extends { capacity?: number | null }>(
  spaces: T[],
  priceOf: (space: T) => number,
): { totalCapacity: number; totalPrice: number } {
  return {
    totalCapacity: spaces.reduce((sum, s) => sum + (s.capacity ?? 0), 0),
    totalPrice: spaces.reduce((sum, s) => sum + priceOf(s), 0),
  };
}
